import 'components/inline-edit/ibm-products-config';
import { SidePanel } from '@carbon/ibm-products';
import {
  Accordion,
  AccordionItem,
  InlineLoading,
  InlineNotification,
  Tag,
  TextArea,
} from '@carbon/react';
import { ChevronDown, ChevronUp } from '@carbon/react/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState, type KeyboardEvent } from 'react';

import { format8 } from 'utils/number';

import {
  slotDetailAtom,
  slotDetailLoadableAtom,
  slotDetailTargetAtom,
} from '../atoms';
import { DeltaIndicator } from '../comparison-table/delta-indicator';
import type {
  SlotDetailConfirmationGroup,
  SlotDetailDifferences,
  SlotDetailInterestPair,
  SlotDetailResponse,
  SlotDetailSubtractCalculation,
} from '../types';
import { STREAM_LABELS, evidenceSourceLabel } from '../utils';
import { useWorkspaceConfirmation } from '../workspace-confirmation-context';
import { FormulaBreakdown } from './formula-breakdown';
import styles from './slot-detail-panel.module.scss';

/**
 * One `NRI: … WI: …` value line, optionally prefixed with a row label
 * (e.g. "At Election", "Current (Live)"). Renders an em-dash for null values.
 *
 * @param {object} props - Component props.
 * @param {string} [props.label] - Optional leading label.
 * @param {SlotDetailInterestPair | null} props.pair - The NRI/WI pair.
 * @return {JSX.Element} The value line.
 */
const InterestRow = ({
  label,
  pair,
}: {
  label?: string;
  pair: SlotDetailInterestPair | null;
}): JSX.Element => (
  <div className={styles.interestRow}>
    {label !== undefined && <span className={styles.rowLabel}>{label}</span>}
    <span className={styles.values}>
      <span>
        NRI: <b>{format8(pair?.nri ?? null, '—')}</b>
      </span>
      <span>
        WI: <b>{format8(pair?.wi ?? null, '—')}</b>
      </span>
    </span>
  </div>
);

/**
 * Column order for the formula breakdown — "At Election (Now)" (the formula
 * frozen at confirm time) first when present, then "Current (Live)" (the live
 * `FormulaDetailsBuilder` output). Mirrors the mock's side-by-side layout.
 */
const FORMULA_COLUMNS = [
  { key: 'atConfirmation', label: 'At Election (Now)' },
  { key: 'current', label: 'Current (Live)' },
] as const;

/**
 * One formula section (NRI or WI) for a subtract: side-by-side "At Election
 * (Now)" / "Current (Live)" columns, each rendering the canonical AM formula
 * breakdown (Tract NRI/WI + UAT NRI/WI) from the BE payload. Columns whose
 * formula the BE didn't supply are dropped — so an unconfirmed slot (no frozen
 * snapshot) shows only the Current (Live) column.
 *
 * @param {object} props - Component props.
 * @param {SlotDetailSubtractCalculation} props.calc - The subtract calc entry.
 * @param {'NRI' | 'WI'} props.calculationType - Which value to break down.
 * @return {JSX.Element | null} The section, or null when no formula exists.
 */
const FormulaSection = ({
  calc,
  calculationType,
}: {
  calc: SlotDetailSubtractCalculation;
  calculationType: 'NRI' | 'WI';
}): JSX.Element | null => {
  const columns = FORMULA_COLUMNS.filter((col) => {
    const formula = calc.formulas[col.key];
    if (formula === null) return false;
    // WI only makes sense when the formula carries a working-interest value
    // (absent for ORRI / no-Allocation-Manager subtracts) — otherwise the
    // whole WI section would be em-dashes.
    if (calculationType === 'WI')
      return formula.nriFormula?.tractWi !== undefined;
    return true;
  });
  if (columns.length === 0) return null;

  return (
    <div className={styles.formulaSection}>
      <h5 className={styles.formulaSectionTitle}>{calculationType}</h5>
      <div className={styles.formulaColumns}>
        {columns.map((col) => {
          const formula = calc.formulas[col.key];
          if (formula === null) return null;
          const pair =
            col.key === 'atConfirmation' ? calc.atConfirmation : calc.current;
          const value =
            calculationType === 'NRI'
              ? (pair?.nri ?? null)
              : (pair?.wi ?? null);
          return (
            <div key={col.key} className={styles.formulaColumn}>
              <div className={styles.formulaColumnLabel}>{col.label}</div>
              <FormulaBreakdown
                formula={formula}
                value={value}
                calculationType={calculationType}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * A single per-subtract calculation card: identity, the At Election / Current
 * value rows, and a chevron that expands the AM formula breakdown — NRI and WI
 * sections, each showing "At Election (Now)" and "Current (Live)" side by side
 * (the mock layout). The chevron is omitted when the BE supplies no formula for
 * this subtract (unconfirmed / non-AM path).
 *
 * @param {object} props - Component props.
 * @param {SlotDetailSubtractCalculation} props.calc - The subtract calc entry.
 * @return {JSX.Element} The card.
 */
const SubtractCard = ({
  calc,
}: {
  calc: SlotDetailSubtractCalculation;
}): JSX.Element => {
  const [expanded, setExpanded] = useState(false);
  const hasFormula =
    calc.formulas.current !== null || calc.formulas.atConfirmation !== null;

  const toggle = (): void => {
    if (hasFormula) setExpanded((prev) => !prev);
  };

  // Whole card header is the click target (only when there's a formula to
  // reveal). Keyboard support: Enter / Space toggle, matching a native button.
  const headerProps = hasFormula
    ? {
        'role': 'button' as const,
        'tabIndex': 0,
        'aria-expanded': expanded,
        'onClick': toggle,
        'onKeyDown': (event: KeyboardEvent<HTMLDivElement>): void => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        },
      }
    : {};

  return (
    <div className={styles.subtractCard}>
      <div
        className={
          hasFormula
            ? styles.subtractHeaderRowClickable
            : styles.subtractHeaderRow
        }
        {...headerProps}
      >
        <div className={styles.subtractSummary}>
          {/* Tract | Subtract | UAT identity, mirroring the comparison table's
              subtracts-table row. tractName / uatName are supplied by a follow-
              on BE change — em-dash until then. */}
          <div className={styles.identityRow}>
            <span>Tract: {calc.tractName ?? calc.tractId ?? '—'}</span>
            <span className={styles.identitySep}>|</span>
            <span>Subtract: {calc.subtractName ?? calc.subtractId}</span>
            <span className={styles.identitySep}>|</span>
            <span>UAT: {calc.uatName ?? '—'}</span>
          </div>
          {calc.atConfirmation !== null && (
            <InterestRow label="At Election" pair={calc.atConfirmation} />
          )}
          <InterestRow label="Current" pair={calc.current} />
          {calc.drifted && (
            <Tag type="warm-gray" size="sm" className={styles.driftTag}>
              Drifted
            </Tag>
          )}
        </div>
        {hasFormula && (
          <span className={styles.chevron} aria-hidden="true">
            {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </span>
        )}
      </div>
      {hasFormula && expanded && (
        <div className={styles.formulaBreakdown}>
          <FormulaSection calc={calc} calculationType="NRI" />
          <FormulaSection calc={calc} calculationType="WI" />
        </div>
      )}
    </div>
  );
};

/**
 * One labelled Differences group (CURRENT (LIVE) / AT ELECTION). The AM-vs-DO
 * rows always render (em-dash until a live DO source exists — INS-9476); the
 * user-entered comparison rows render only when the BE populates them (i.e. the
 * active confirmation is user-entered). `base` is the AM operand, used by
 * `DeltaIndicator` only to compute the percentage when the diff is non-null.
 *
 * @param {object} props - Component props.
 * @return {JSX.Element} The group.
 */
const DifferenceGroup = ({
  label,
  diffs,
  amPair,
}: {
  label: string;
  diffs: SlotDetailDifferences;
  amPair: SlotDetailInterestPair | null;
}): JSX.Element => {
  const rows: { label: string; diff: number | null; base: number | null }[] = [
    {
      label: 'Allocation Manager vs Division Order NRI:',
      diff: diffs.amVsDoNri,
      base: amPair?.nri ?? null,
    },
    {
      label: 'Allocation Manager vs Division Order WI:',
      diff: diffs.amVsDoWi,
      base: amPair?.wi ?? null,
    },
  ];
  if (diffs.userVsAmNri !== null)
    rows.push({
      label: 'User Entered vs Allocation Manager NRI:',
      diff: diffs.userVsAmNri,
      base: amPair?.nri ?? null,
    });
  if (diffs.userVsAmWi !== null)
    rows.push({
      label: 'User Entered vs Allocation Manager WI:',
      diff: diffs.userVsAmWi,
      base: amPair?.wi ?? null,
    });
  if (diffs.userVsDoNri !== null)
    rows.push({
      label: 'User Entered vs Division Order NRI:',
      diff: diffs.userVsDoNri,
      base: amPair?.nri ?? null,
    });
  if (diffs.userVsDoWi !== null)
    rows.push({
      label: 'User Entered vs Division Order WI:',
      diff: diffs.userVsDoWi,
      base: amPair?.wi ?? null,
    });

  return (
    <div className={styles.diffGroup}>
      <h5 className={styles.groupLabel}>{label}</h5>
      {rows.map((row) => (
        <div key={row.label} className={styles.diffRow}>
          <span>{row.label}</span>
          <DeltaIndicator diff={row.diff} base={row.base} />
        </div>
      ))}
    </div>
  );
};

/**
 * One Elected Source group (INS-10204): a slot's certified streams grouped by
 * `[source, confirmedNri, confirmedWi]`. Renders the source label, the group's
 * certified NRI/WI, its stream tags, its per-tract Subtract-Level Calculations,
 * and its at-election Differences. A uniform / single-stream slot has exactly
 * one group, so this renders identically to the pre-group single block.
 *
 * @param {object} props - Component props.
 * @param {SlotDetailConfirmationGroup} props.group - The confirmation group.
 * @param {SlotDetailInterestPair | null} props.amAtConfirmation - The slot's
 *   at-election AM pair (snapshot), the base for the group's user-vs-AM deltas.
 * @return {JSX.Element} The group section.
 */
const ConfirmationGroupSection = ({
  group,
  amAtConfirmation,
}: {
  group: SlotDetailConfirmationGroup;
  amAtConfirmation: SlotDetailInterestPair | null;
}): JSX.Element => (
  <section className={styles.section}>
    <h3 className={styles.sectionTitle}>Elected Source</h3>
    <h4 className={styles.blockTitle}>{evidenceSourceLabel(group)}</h4>
    <InterestRow pair={{ nri: group.confirmedNri, wi: group.confirmedWi }} />
    {group.streams.length > 0 && (
      <div className={styles.streamRow}>
        <span className={styles.subLabel}>Streams</span>
        <div className={styles.streamTags}>
          {group.streams.map((stream) => (
            <Tag key={stream} type="blue" size="sm">
              {STREAM_LABELS[stream]}
            </Tag>
          ))}
        </div>
      </div>
    )}
    {(group.note ?? '').trim().length > 0 && (
      // Read-only, but boxed like the certify note field so the eye catches it.
      <TextArea
        id={`detail-note-${group.source}-${group.streams.join('-')}`}
        className={styles.noteField}
        labelText="Supporting note"
        value={group.note ?? ''}
        rows={3}
        readOnly
      />
    )}
    {group.subtractCalculations.length > 0 && (
      <>
        <h5 className={styles.groupLabel}>Subtract-Level Calculations</h5>
        {group.subtractCalculations.map((calc) => (
          // Per-UAT grain (ONCALL-668): one calc per (subtract, UAT), so key on
          // both — two UATs of a subtract would collide on subtractId alone.
          <SubtractCard
            key={`${calc.subtractId}-${calc.uatId ?? 'no-uat'}`}
            calc={calc}
          />
        ))}
      </>
    )}
    <DifferenceGroup
      label="AT ELECTION"
      diffs={group.differences}
      amPair={amAtConfirmation}
    />
  </section>
);

/**
 * Renders the loaded Slot Details body — every section the E-12 payload carries
 * (one Elected Source group per certification, Allocation Manager, unconfirmed
 * subtract-level calculations, Division Order, Differences, and linked DO
 * documents). Sections that the payload empties out (no confirmation, no live DO
 * source, etc.) are omitted rather than shown empty.
 *
 * @param {object} props - Component props.
 * @param {SlotDetailResponse} props.detail - The loaded payload.
 * @return {JSX.Element} The body.
 */
const SlotDetailBody = ({
  detail,
}: {
  detail: SlotDetailResponse;
}): JSX.Element => {
  const am = detail.allocationManager;
  return (
    <div className={styles.sections}>
      {detail.confirmationGroups.map((group, index) => (
        <ConfirmationGroupSection
          // Groups are partitioned by [source, nri, wi] over the slot's streams,
          // so include confirmedNri/Wi in the key: two same-source groups can
          // share a first stream (e.g. oil re-certified at a different NRI after
          // a reopen), and source+stream alone would then collide.
          key={`${group.source}-${group.confirmedNri ?? 'x'}-${
            group.confirmedWi ?? 'x'
          }-${group.streams[0] ?? index}`}
          group={group}
          amAtConfirmation={am.atConfirmation}
        />
      ))}

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Allocation Manager</h3>
        {am.atConfirmation !== null && (
          <InterestRow label="At Election" pair={am.atConfirmation} />
        )}
        <InterestRow label="Current (Live)" pair={am.current} />
        {am.drifted && (
          <Tag type="warm-gray" size="sm" className={styles.driftTag}>
            Drifted from snapshot
          </Tag>
        )}
      </section>

      {detail.subtractCalculations.length > 0 && (
        <section className={styles.section}>
          <h5 className={styles.groupLabel}>Subtract-Level Calculations</h5>
          {detail.subtractCalculations.map((calc) => (
            <SubtractCard
              key={`${calc.subtractId}-${calc.uatId ?? 'no-uat'}`}
              calc={calc}
            />
          ))}
        </section>
      )}

      {detail.divisionOrder !== null && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Division Order</h3>
          <InterestRow
            pair={{
              nri: detail.divisionOrder.nri,
              wi: detail.divisionOrder.wi,
            }}
          />
          {detail.divisionOrder.tracts.length > 0 && (
            <table className={styles.doTable}>
              <thead>
                <tr>
                  <th>Tract</th>
                  <th>Type</th>
                  <th>Decimal</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {detail.divisionOrder.tracts.map((tract) => (
                  <tr key={tract.tractId}>
                    <td>{tract.tractName ?? tract.tractId}</td>
                    <td>{tract.type ?? '—'}</td>
                    <td>{format8(tract.decimal ?? null, '—')}</td>
                    <td>{tract.source ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Differences</h3>
        {/* Live, stream-agnostic deltas (AM vs DO). The per-election user-vs-AM
            comparisons render inside each Elected Source group above. */}
        <DifferenceGroup
          label="CURRENT (LIVE)"
          diffs={detail.differences.current}
          amPair={am.current}
        />
      </section>

      {detail.divisionOrderDocuments.length > 0 && (
        <Accordion size="sm">
          <AccordionItem
            title={`Division Order Documents (${detail.divisionOrderDocuments.length})`}
          >
            <ul className={styles.docList}>
              {detail.divisionOrderDocuments.map((doc) => (
                <li key={doc.id}>
                  <a href={doc.href} target="_blank" rel="noreferrer">
                    {doc.filename ?? `Document ${doc.id}`}
                  </a>
                  {doc.pageCount !== null && (
                    <span className={styles.pageCount}>
                      {' '}
                      · {doc.pageCount} pages
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};

/**
 * Slot Details side panel (E-12). Opened from the comparison table's "Show
 * details" action, which writes `slotDetailTargetAtom`; the panel lazy-fetches
 * the deep payload for that slot on open and renders the read-only breakdown.
 *
 * @return {JSX.Element} The rendered side panel (hidden when closed).
 */
export const SlotDetailPanel = (): JSX.Element => {
  const { workspaceId } = useWorkspaceConfirmation();
  const [target, setTarget] = useAtom(slotDetailTargetAtom);
  const loadable = useAtomValue(slotDetailLoadableAtom);
  const fetchDetail = useSetAtom(slotDetailAtom);

  // Fire the fetch when a row is targeted (panel opens / target switches).
  useEffect(() => {
    if (target !== null) {
      fetchDetail(workspaceId, target.workspaceSlotId);
    }
  }, [target, workspaceId, fetchDetail]);

  const open = target !== null;
  const handleClose = (): void => setTarget(null);

  const detail = loadable.state === 'hasData' ? loadable.data : null;
  // Guard against the stale-data frame between writing `target` and the effect
  // firing: only treat the payload as ready when it matches the open row.
  const matched =
    detail !== null &&
    target !== null &&
    detail.workspaceSlotId === target.workspaceSlotId;
  const isLoading = open && !matched && loadable.state !== 'hasError';
  const error =
    loadable.state === 'hasError'
      ? loadable.error instanceof Error
        ? loadable.error.message
        : 'Failed to load slot details'
      : null;

  return (
    <SidePanel
      open={open}
      onRequestClose={handleClose}
      title={target !== null ? `Details ${target.slotId}` : ''}
      subtitle="View slot interest details and formula breakdown."
      placement="right"
      size="lg"
      animateTitle={false}
      slideIn
      selectorPageContent="#ic-workspace-main"
    >
      <div className={styles.body}>
        {isLoading && <InlineLoading description="Loading slot details…" />}
        {error !== null && (
          <InlineNotification
            className={styles.notification}
            kind="error"
            lowContrast
            hideCloseButton
            title={error}
          />
        )}
        {!isLoading && error === null && matched && (
          <SlotDetailBody detail={detail} />
        )}
      </div>
    </SidePanel>
  );
};
